import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { PublicBookingService } from '../../core/services/public-booking.service';
import { environment } from '../../environment';

@Component({
  selector: 'app-public-ticket',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './public-ticket.component.html',
  styleUrls: ['./public-ticket.component.css']
})
export class PublicTicketComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private publicBookingService = inject(PublicBookingService);

  ticket: any = null;
  token = '';
  isLoading = false;
  isOpeningWalletPass = false;
  error = '';
  walletError = '';

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.loadTicket();
  }

  loadTicket(): void {
    if (!this.token) {
      this.error = 'Ticket link is missing its access token.';
      return;
    }

    this.isLoading = true;
    this.error = '';
    this.publicBookingService.getTicket(this.token).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: ticket => this.ticket = ticket,
      error: error => this.error = this.extractError(error, 'We could not load this e-ticket. Check the link or booking reference.')
    });
  }

  getReference(): string {
    return this.pick(['bookingReference', 'reference', 'bookingRef']) || '-';
  }

  getTripName(): string {
    return this.pick(['tripName', 'routeName', 'trip.routeName', 'trip.tripName']) || 'Trip';
  }

  getOperatorName(): string {
    return this.pick([
      'operatorName',
      'operatorCode',
      'organisationName',
      'orgName',
      'operator.name',
      'operator.orgName',
      'organisation.organisationName',
      'organisation.orgName',
      'trip.operatorName',
      'trip.organisationName',
      'trip.orgName',
      'trip.operator.name',
      'trip.operator.orgName',
      'trip.organisation.organisationName',
      'trip.organisation.orgName'
    ]) || 'Operator';
  }

  getOperatorLogo(): string {
    return this.pick([
      'operatorLogoUrl',
      'orgUrl',
      'operatorLogo',
      'organisationLogo',
      'orgLogo',
      'operator.logo',
      'operator.operatorLogoUrl',
      'operator.orgUrl',
      'operator.operatorLogo',
      'operator.orgLogo',
      'organisation.logo',
      'organisation.operatorLogoUrl',
      'organisation.orgUrl',
      'organisation.organisationLogo',
      'organisation.orgLogo',
      'trip.operatorLogoUrl',
      'trip.orgUrl',
      'trip.operatorLogo',
      'trip.organisationLogo',
      'trip.orgLogo',
      'trip.operator.logo',
      'trip.operator.operatorLogoUrl',
      'trip.operator.orgUrl',
      'trip.operator.operatorLogo',
      'trip.operator.orgLogo',
      'trip.organisation.logo',
      'trip.organisation.operatorLogoUrl',
      'trip.organisation.orgUrl',
      'trip.organisation.organisationLogo',
      'trip.organisation.orgLogo'
    ]);
  }

  getOperatorInitials(): string {
    return this.getOperatorName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('') || 'RS';
  }

  getDeparture(): string {
    return this.pick(['departureTime', 'tripStartTime', 'trip.tripStartTime', 'trip.departureTime']) || '';
  }

  getArrival(): string {
    return this.pick(['arrivalTime', 'tripEndTime', 'trip.tripEndTime', 'trip.arrivalTime']) || '';
  }

  getSeats(): string {
    const seats = this.asArray(this.pickValue(['selectedSeats', 'seatNumbers', 'bookedSeatNumbers']));
    return seats.length ? seats.join(', ') : String(this.pick(['seatsBooked']) || 'Reserved');
  }

  getPassenger(): string {
    return this.pick(['passengerName', 'passenger.name']) || 'Passenger';
  }

  getContact(): string {
    return this.pick(['passengerEmail', 'passengerPhone', 'passenger.email', 'passenger.phone']) || 'Contact captured with booking';
  }

  getPaymentStatus(): string {
    const status = this.pick(['paymentStatus']) || 'PayAtTicketOffice';
    return status.toLowerCase() === 'payatticketoffice' ? 'Pay at ticket office' : status;
  }

  getWalletPassUrl(): string {
    const rawUrl = this.pick([
      'appleWalletUrl',
      'appleWalletPassUrl',
      'walletPassUrl',
      'pkPassUrl',
      'ticket.appleWalletUrl',
      'ticket.appleWalletPassUrl',
      'ticket.walletPassUrl',
      'ticket.pkPassUrl'
    ]);

    return this.normalizeBackendUrl(rawUrl);
  }

  openWalletPass(event: Event): void {
    event.preventDefault();
    const walletUrl = this.getWalletPassUrl();
    this.walletError = '';

    if (!walletUrl) {
      this.walletError = 'Apple Wallet is not available for this ticket yet.';
      return;
    }

    this.isOpeningWalletPass = true;
    fetch(walletUrl, { method: 'HEAD' }).then(response => {
      if (response.ok || response.status === 405) {
        window.location.href = walletUrl;
        return;
      }

      if (response.status === 404) {
        this.walletError = 'Apple Wallet pass is not available for this ticket yet.';
        return;
      }

      this.walletError = 'Unable to download the Apple Wallet pass right now.';
    }).catch(() => {
      this.walletError = 'Unable to download the Apple Wallet pass right now.';
    }).finally(() => {
      this.isOpeningWalletPass = false;
    });
  }

  getQrCodeUrl(): string {
    const value = this.pick([
      'qrCodeUrl',
      'qrCodeImageUrl',
      'ticketQrCodeUrl',
      'qrUrl',
      'qrCode',
      'ticket.qrCodeUrl'
    ]);

    if (value.startsWith('data:image') || /\.(png|jpe?g|gif|svg|webp)(\?|#|$)/i.test(value)) {
      return value;
    }

    return '';
  }

  getQrCodePayload(): string {
    return this.pick([
      'qrCodePayload',
      'ticket.qrCodePayload',
      'qrPayload',
      'verificationPayload',
      'eTicketUrl',
      'ticketUrl'
    ]) || this.getVerificationCode();
  }

  getQrDisplayCode(): string {
    const payload = this.getQrCodePayload();
    const ticketMatch = payload.match(/\/tickets\/([^/?#]+)/i);
    if (ticketMatch) {
      return decodeURIComponent(ticketMatch[1]);
    }

    return payload || this.getReference();
  }

  getVerificationCode(): string {
    return this.pick(['qrCodePayload', 'ticket.qrCodePayload']) || this.token || this.getReference();
  }

  getQrCells(): boolean[] {
    const seed = this.getQrCodePayload() || this.getVerificationCode() || this.getReference();
    const values = Array.from({ length: 49 }, (_, index) => {
      const code = seed.charCodeAt(index % Math.max(seed.length, 1)) || 17;
      return (code + index * 7) % 3 !== 0;
    });

    values[0] = true;
    values[1] = true;
    values[7] = true;
    values[8] = false;
    values[40] = true;
    values[48] = true;
    return values;
  }

  downloadTicketPdf(): void {
    const printWindow = window.open('', '_blank', 'width=820,height=1100');
    if (!printWindow) {
      this.error = 'Please allow popups to download the ticket as a PDF.';
      return;
    }

    printWindow.document.write(this.buildDownloadHtml(true));
    printWindow.document.close();
  }

  printTicket(): void {
    window.print();
  }

  private pick(paths: string[]): string {
    const value = this.pickValue(paths);
    return value === undefined || value === null ? '' : String(value);
  }

  private pickValue(paths: string[]): unknown {
    for (const path of paths) {
      const value = path.split('.').reduce((current: any, key) => current?.[key], this.ticket);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return undefined;
  }

  private asArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    const collection = value as { $values?: unknown[] } | null | undefined;
    return Array.isArray(collection?.$values) ? collection.$values : [];
  }

  private extractError(error: any, fallback: string): string {
    return error?.error?.message
      || error?.error?.title
      || (typeof error?.error === 'string' ? error.error : '')
      || error?.message
      || fallback;
  }

  private normalizeBackendUrl(rawUrl: string): string {
    const value = String(rawUrl || '').trim();
    if (!value) {
      return '';
    }

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    if (value.startsWith('/api/')) {
      return `${this.getApiOrigin()}${value}`;
    }

    if (value.startsWith('/')) {
      return `${this.getApiOrigin()}${value}`;
    }

    return value;
  }

  private getApiOrigin(): string {
    try {
      return new URL(environment.apiUrl).origin;
    } catch {
      return window.location.origin;
    }
  }

  private buildDownloadHtml(autoPrint = false): string {
    const logo = this.getOperatorLogo();
    const qrCode = this.getQrCodeUrl();
    const qrDisplayCode = this.getQrDisplayCode();
    const qrMarkup = qrCode
      ? `<img class="qr-img" src="${this.escapeHtml(qrCode)}" alt="Ticket QR code">`
      : `<div class="qr-fallback">${this.getQrCells().map(active => `<span class="${active ? 'active' : ''}"></span>`).join('')}</div>`;
    const printScript = autoPrint ? '<script>window.onload=function(){window.focus();window.print();};</script>' : '';

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${this.escapeHtml(this.getReference())}</title>
  <style>
    @page{size:A4 portrait;margin:14mm}
    body{background:#fff;color:#172033;font-family:Arial,sans-serif;margin:0;padding:0}
    .ticket{background:#fff;border:1px solid #e4d8c5;border-radius:8px;margin:0 auto;max-width:620px;overflow:hidden}
    header{align-items:center;background:#4b1305;color:#fff;display:flex;gap:14px;justify-content:space-between;padding:18px}
    .brand{align-items:center;display:flex;gap:12px}.brand img{background:#fff;border-radius:6px;height:48px;object-fit:contain;padding:5px;width:48px}.logo-fallback{background:#f6c21a;border-radius:6px;color:#4b1305;font-weight:900;padding:14px}
    h1,h2,p{margin:0}.ref{font-size:22px;font-weight:900}.muted{color:#6b7280;font-size:12px;font-weight:700}.body{display:grid;gap:0;grid-template-columns:1fr}
    .main{padding:18px}.route{border-bottom:1px dashed #d7c8b4;margin-bottom:14px;padding-bottom:14px}.route h2{font-size:20px;margin:4px 0}
    dl{display:grid;gap:10px;grid-template-columns:repeat(2,1fr);margin:0}dt{color:#6b7280;font-size:11px;font-weight:900;text-transform:uppercase}dd{font-weight:800;margin:3px 0 0}
    aside{border-top:1px dashed #d7c8b4;display:grid;gap:10px;justify-items:center;padding:18px;text-align:center}.qr-img,.qr-fallback{height:132px;width:132px}.qr-fallback{background:#fff;border:8px solid #fff;box-shadow:0 0 0 1px #172033;display:grid;grid-template-columns:repeat(7,1fr)}.qr-fallback span.active{background:#172033}
    .status{background:#fff8d8;border-radius:999px;color:#4b1305;font-size:12px;font-weight:900;padding:7px 10px}
    @media print{body{background:#fff;padding:0}.ticket{box-shadow:none;break-inside:avoid}}
  </style>
</head>
<body>
  <article class="ticket">
    <header>
      <div class="brand">${logo ? `<img src="${this.escapeHtml(logo)}" alt="">` : `<span class="logo-fallback">${this.escapeHtml(this.getOperatorInitials())}</span>`}<div><p>${this.escapeHtml(this.getOperatorName())}</p><div class="ref">${this.escapeHtml(this.getReference())}</div></div></div>
      <span class="status">${this.escapeHtml(this.getPaymentStatus())}</span>
    </header>
    <div class="body">
      <section class="main">
        <div class="route"><p class="muted">E-ticket</p><h2>${this.escapeHtml(this.getTripName())}</h2><p>${this.escapeHtml(this.getDeparture())}</p></div>
        <dl><div><dt>Passenger</dt><dd>${this.escapeHtml(this.getPassenger())}</dd></div><div><dt>Contact</dt><dd>${this.escapeHtml(this.getContact())}</dd></div><div><dt>Seats</dt><dd>${this.escapeHtml(this.getSeats())}</dd></div><div><dt>Payment</dt><dd>${this.escapeHtml(this.getPaymentStatus())}</dd></div></dl>
      </section>
      <aside>${qrMarkup}<p class="muted">Verification code</p><strong>${this.escapeHtml(qrDisplayCode)}</strong></aside>
    </div>
  </article>
  ${printScript}
</body>
</html>`;
  }

  private escapeHtml(value: unknown): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
